import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { useEffect, useRef, useState } from "react";

const MEMBRESIA_LEVEL1_EXPIRED = "membresia-telegram-level1-expired";
const MEMBRESIA_LEVEL2_EXPIRED = "membresia-telegram-level2-expired";

// MercadoPago subscription links — set via Vercel env vars (VITE_MP_TELEGRAM_PLAN_LEVEL1, VITE_MP_TELEGRAM_PLAN_LEVEL2)
const MP_BASE = "https://www.mercadopago.com.ar/subscriptions/checkout?preapproval_plan_id=";
const SUBSCRIPTION_LINK_LEVEL1 = import.meta.env.VITE_MP_TELEGRAM_PLAN_LEVEL1
  ? `${MP_BASE}${import.meta.env.VITE_MP_TELEGRAM_PLAN_LEVEL1}`
  : "";
const SUBSCRIPTION_LINK_LEVEL2 = import.meta.env.VITE_MP_TELEGRAM_PLAN_LEVEL2
  ? `${MP_BASE}${import.meta.env.VITE_MP_TELEGRAM_PLAN_LEVEL2}`
  : "";

type OfferLevel = 1 | 2;

/**
 * Membresía Telegram Page
 * Level 1: 60s (1 min) with $39,999/month.
 * When level 1 expires: "Pedir uno nuevo" available ONLY in same session.
 * If user refreshes without clicking "Pedir uno nuevo" → fully disabled (no second chance).
 * Level 2: $49,999/month, 60s. When expired → fully disabled.
 */

const getInitialState = (): {
  level: OfferLevel;
  countdown: number;
  isButtonEnabled: boolean;
  level1Expired: boolean;
  level2Expired: boolean;
} => {
  if (typeof window === "undefined") {
    return {
      level: 1,
      countdown: 60,
      isButtonEnabled: true,
      level1Expired: false,
      level2Expired: false,
    };
  }
  const level2Expired = localStorage.getItem(MEMBRESIA_LEVEL2_EXPIRED) === "1";
  const level1Expired = localStorage.getItem(MEMBRESIA_LEVEL1_EXPIRED) === "1";
  // If level2Expired is set (either from level 2 expiring OR from not clicking "Pedir uno nuevo"), fully disabled
  if (level2Expired) {
    return {
      level: 2,
      countdown: 0,
      isButtonEnabled: false,
      level1Expired: true,
      level2Expired: true,
    };
  }
  // Fresh visit
  return {
    level: 1,
    countdown: 60,
    isButtonEnabled: true,
    level1Expired: false,
    level2Expired: false,
  };
};

const MembresiaTelegram = () => {
  const initial = getInitialState();
  const [level, setLevel] = useState<OfferLevel>(() => initial.level);
  const [countdown, setCountdown] = useState(() => initial.countdown);
  const [isButtonEnabled, setIsButtonEnabled] = useState(
    () => initial.isButtonEnabled
  );
  const countdownActiveRef = useRef(initial.isButtonEnabled);

  const canRequestNew = initial.level1Expired && !initial.level2Expired;
  const [showRequestNew, setShowRequestNew] = useState(canRequestNew);

  const subscriptionLink =
    level === 1 ? SUBSCRIPTION_LINK_LEVEL1 : SUBSCRIPTION_LINK_LEVEL2;

  // Countdown: after 60 seconds disable the button for current level
  useEffect(() => {
    if (!countdownActiveRef.current) return;
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
    countdownActiveRef.current = false;
    if (level === 1) {
      // Set BOTH expired: if user refreshes without clicking "Pedir uno nuevo", page stays disabled
      localStorage.setItem(MEMBRESIA_LEVEL1_EXPIRED, "1");
      localStorage.setItem(MEMBRESIA_LEVEL2_EXPIRED, "1");
      setIsButtonEnabled(false);
      setShowRequestNew(true); // Only available in THIS session
    } else {
      localStorage.setItem(MEMBRESIA_LEVEL2_EXPIRED, "1");
      setIsButtonEnabled(false);
    }
  }, [level, countdown]);

  const handleRequestNew = () => {
    // Give them a real chance at level 2: remove the "preemptive" level2Expired
    localStorage.removeItem(MEMBRESIA_LEVEL2_EXPIRED);
    setShowRequestNew(false);
    setLevel(2);
    setCountdown(60);
    setIsButtonEnabled(true);
    countdownActiveRef.current = true;
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="text-center space-y-8 max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src={HEADER_PROFILE_IMAGE_URL}
            alt={BRAND_PERSON_NAME}
            className="w-32 h-32 rounded-full border-4 border-pink-500/50 shadow-2xl shadow-pink-500/20"
          />
        </div>

        {/* Text */}
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-4">
          Te invito a mi comunidad privada
        </h1>

        {/* Countdown message */}
        {isButtonEnabled ? (
          <p className="text-gray-400 text-sm mb-2">
            Se deshabilitará en {countdown} segundo{countdown !== 1 ? "s" : ""}
            {level === 2 && (
              <span className="block mt-1 text-amber-400/90 text-xs">
                (Precio más alto)
              </span>
            )}
          </p>
        ) : (
          <p className="text-gray-500 text-sm mb-2">
            El botón ya no está disponible
          </p>
        )}

        {/* Disclaimer */}
        <p className="text-gray-500 text-xs max-w-sm mx-auto mb-2">
          Si se activa el botón de nuevo, incrementará el valor.
        </p>

        {/* Pedir uno nuevo (solo cuando nivel 1 expiró y nivel 2 no) */}
        {showRequestNew && !isButtonEnabled && (
          <button
            type="button"
            onClick={handleRequestNew}
            className="w-full max-w-xs mx-auto py-3 px-6 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold ui-transition-colors duration-200 mb-4"
          >
            Pedir uno nuevo
          </button>
        )}

        {/* Subscription Button */}
        <div className="relative flex justify-center mt-4 min-h-[48px]">
          {isButtonEnabled ? (
            <a
              href={subscriptionLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center py-3 px-8 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold ui-transition-colors duration-200"
            >
              Suscribirme ahora
            </a>
          ) : (
            !showRequestNew && (
              <div
                className="flex items-center justify-center min-h-[48px]"
                aria-hidden="true"
              >
                <span className="text-gray-400 text-sm">Deshabilitado</span>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default MembresiaTelegram;
